import TaskActionsMenu from './TaskActionsMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMusic,
  faCode,
  faHouse,
  faFileLines,
  faHeartPulse,
  faDumbbell,
  faBookOpen,
  faPen,
  faFeather,
  faPalette,
  faPeopleRoof,
  faUserGroup,
  faUtensils,
  faCartShopping,
  faCar,
  faCoins,
  faDesktop,
  faGlobe,
  faMobileScreen,
  faLeaf,
  faTag,
  faEllipsisVertical,
  faGraduationCap,
  faLandmark,
  faWineGlass,
  faThumbsDown
} from '@fortawesome/free-solid-svg-icons';

const themeIcons = {
  music: faMusic,
  code: faCode,
  house: faHouse,
  'file-lines': faFileLines,
  'heart-pulse': faHeartPulse,
  dumbbell: faDumbbell,
  'book-open': faBookOpen,
  pen: faPen,
  feather: faFeather,
  palette: faPalette,
  'people-roof': faPeopleRoof,
  'user-group': faUserGroup,
  utensils: faUtensils,
  'cart-shopping': faCartShopping,
  car: faCar,
  coins: faCoins,
  desktop: faDesktop,
  globe: faGlobe,
  'mobile-screen': faMobileScreen,
  leaf: faLeaf,
  cours: faGraduationCap,
  landmark: faLandmark,
  'wine-glass': faWineGlass,
  annule : faThumbsDown
};

export default function TaskCard({
  task,
  position,
  isSelected,
  isMenuOpen,
  parseDescriptionLines,
  onDragStart,
  onDragEnd,
  onToggleStatus,
  onEdit,
  onToggleDescriptionCheckbox,
  onSetPriority,
  onOrderKeyDown,
  onToggleMenu,
  onToggleSelection,
  onCopyTitle,
  onPrint,
  onDelete,
  onCloseMenu,
  onOpenThemeMenu,
}) {
  const priority = Math.max(0, Math.min(4, Number(task?.important) || 0));

  const selectedThemeIcon =
    task?.theme_icon && themeIcons[task.theme_icon]
      ? themeIcons[task.theme_icon]
      : faTag;

  const hasTheme = Boolean(task?.theme_icon);



  return (
    <div className="task-wrapper">
      <div
        data-task-id={task.id}
        className={`task-mini ${task.status || 'todo'} ${
          isSelected ? 'selected' : ''
        }`}
        draggable={!isSelected}
        onDragStart={() => onDragStart(task)}
        onDragEnd={onDragEnd}
        onClick={(event) => {
          event.stopPropagation();
          onToggleStatus(task);
        }}
        onDoubleClick={(event) => {
          event.stopPropagation();
          onEdit(task);
        }}
      >
        <div className="task-mini-header">
          <div className="task-mini-title-row">
            <span className="task-mini-title">{task.title}</span>
          </div>
        </div>

        {task.description?.trim() && (
          <div className="task-mini-body">
            <div className="task-mini-desc">
              {parseDescriptionLines(task.description).map(
                (line, lineIndex) => {
                  if (line.type === 'checkbox') {
                    return (
                      <label
                        key={`${task.id}-desc-line-${lineIndex}`}
                        className={`task-desc-checkbox-line ${
                          line.checked ? 'is-checked' : ''
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleDescriptionCheckbox(task, lineIndex);
                        }}
                      >
                        <span
                          className={`task-desc-checkbox-box ${
                            line.checked ? 'is-checked' : ''
                          }`}
                        >
                          {line.checked ? '✓' : ''}
                        </span>

                        <span className="task-desc-checkbox-text">
                          {line.text || ' '}
                        </span>
                      </label>
                    );
                  }

                  return (
                    <div
                      key={`${task.id}-desc-line-${lineIndex}`}
                      className={`task-desc-text-line ${
                        line.text ? '' : 'is-empty'
                      }`.trim()}
                    >
                      {line.text || '\u00a0'}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}

        <div className="task-mini-toolbar">
          <div
            className="task-mini-tools"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              position: 'relative',
            }}
          >
            {/* Ordre : on garde l'input pour conserver le changement d'ordre */}
            <input
              key={`${task.id}-${task.ordre_affichage ?? position}`}
              type="number"
              min="1"
              className="task-order-badge"
              defaultValue={task.ordre_affichage ?? position}
              title="Ordre d'affichage"
              aria-label="Ordre d'affichage"
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => onOrderKeyDown(event, task.id)}
              style={{
                position: 'absolute',
                left: '4px',
              }}
            />

            {/* Thème */}
            <button
              type="button"
              className={`task-theme-btn ${
                hasTheme ? 'has-theme' : 'no-theme'
              }`}
              title={
                task.theme_name
                  ? `Thème : ${task.theme_name}`
                  : 'Choisir un thème'
              }
              onClick={(event) => {
                event.stopPropagation();

                if (typeof onOpenThemeMenu === 'function') {
                  onOpenThemeMenu(task);
                }
              }}
            >
              <FontAwesomeIcon icon={selectedThemeIcon} />
            </button>

            {/* Étoiles */}
            <div
              role="group"
              aria-label={`Priorité : ${priority} sur 4`}
              onClick={(event) => event.stopPropagation()}
              style={{
                marginLeft: 'auto',
                display: 'flex',
                gap: '2px',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              {[1, 2, 3, 4].map((level) => {
                const isFilled = level <= priority;

                return (
                  <span
                    key={level}
                    role="button"
                    tabIndex={0}
                    className={`priority-star ${
                      isFilled ? 'filled' : 'empty'
                    }`}
                    title={`${level} étoile${level > 1 ? 's' : ''}`}
                    aria-label={`Définir la priorité à ${level} sur 4`}
                    aria-pressed={priority === level}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSetPriority(task, level);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onSetPriority(task, level);
                      }
                    }}
                  >
                    {isFilled ? '★' : '☆'}
                  </span>
                );
              })}
            </div>

            {/* Menu + actions */}
            <div
              style={{
                marginLeft: '8px',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                className={`more-btn ${isMenuOpen ? 'active' : ''}`}
                title="Autres actions"
                aria-label="Autres actions"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleMenu(task.id);
                }}
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>

              {isMenuOpen && (
                <TaskActionsMenu
                  task={task}
                  isSelected={isSelected}
                  onToggleSelection={onToggleSelection}
                  onCopyTitle={onCopyTitle}
                  onPrint={onPrint}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onClose={onCloseMenu}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
